import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { isNil } from "lodash";
import { Icon, RangeNumberInput, RangeNumberInputProps } from "@evergis/ui";
import { useDebouncedCallback } from "@evergis/react";

import { StyledIconButton } from "./styled";
import { FILTER_UPDATE_DEBOUNCE } from "./constants";
import { getConfigFilter } from "../../utils/getConfigFilter";
import { getDate } from "../../../../utils/date";
import { useWidgetContext } from "../../../Dashboard/hooks/useWidgetContext";
import { WidgetFilterProps } from "./types";

export const RangeDateFilter: FC<WidgetFilterProps> = ({ type, filter, config }) => {
  const { t } = useTranslation("featureCard");
  const { changeFilters } = useWidgetContext(type);
  const updateDebounce = useDebouncedCallback(FILTER_UPDATE_DEBOUNCE);

  const { filters: configFilters } = config;
  const { filterName, label, minValue, maxValue, withTime } = filter.options;
  const isInit = useRef(false);

  const [value, setValue] = useState([undefined, undefined] as [Date, Date]);
  const minDate = useMemo(() => getDate(minValue), [minValue]);
  const maxDate = useMemo(() => getDate(maxValue), [maxValue]);

  const isEmpty = isNil(value?.[0]) && isNil(value?.[1]);
  const isDefault = value?.[0] === minDate && value?.[1] === maxDate;

  const reset = useCallback(() => {
    setValue([minDate, maxDate] as [Date, Date]);
    changeFilters({ [filterName]: { value: [minDate, maxDate], min: minDate, max: maxDate } });
  }, [changeFilters, filterName, maxDate, minDate]);

  useEffect(() => {
    if (!isNil(minValue) && !isNil(maxValue)) {
      reset();
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!isInit.current) {
      isInit.current = true;

      return;
    }

    updateDebounce(() => {
      changeFilters({
        [filterName]: {
          value,
          min: value[0],
          max: value[1],
        },
      });
    });
  }, [value]); // eslint-disable-line

  const configFilter = getConfigFilter(filterName, configFilters);

  if (!configFilter) return null;

  return (
    <RangeNumberInput
      label={label || t("interval")}
      selectedOption={{ range: true }}
      minDate={minDate}
      maxDate={maxDate}
      value={value}
      zIndex={100}
      withTime={withTime}
      prefix={<Icon kind="date" disabled />}
      suffix={!isEmpty && !isDefault && <StyledIconButton kind="error" onClick={reset} />}
      isDate
      onChange={setValue as RangeNumberInputProps<Date>["onChange"]}
    />
  );
};
